import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { TextFieldProps, IconButtonProps } from '@material-ui/core';
import { VseHtmlInput } from './VseHtmlInput';
import { VseTinymceEditorDialogArgs } from './VseTinymceEditorDialog';

const useStyles = makeStyles(() => ({
  editButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  }
}));

interface VseHtmlFieldProps {
  mode: VseTinymceEditorDialogArgs['mode'],
  iconColor?: IconButtonProps['color'],
  iconSize?: IconButtonProps['size'],
  instruction?: VseTinymceEditorDialogArgs['instruction'],
  onEditorDialogReqested?: (args: VseTinymceEditorDialogArgs) => void,
  onUpdated: (content?: string) => void,
}

// reference: https://github.com/mui/material-ui/blob/v4.12.4/packages/material-ui/src/TextField/TextField.js
export function VseHtmlField(props: TextFieldProps & VseHtmlFieldProps) {
  const classes = useStyles();
  const {
    autoComplete,
    autoFocus = false,
    children,
    className,
    color = 'primary',
    defaultValue,
    disabled = false,
    error = false,
    FormHelperTextProps,
    fullWidth = false,
    helperText,
    hiddenLabel,
    iconColor = 'secondary',
    iconSize = 'small',
    id,
    InputLabelProps,
    inputProps,
    InputProps,
    inputRef,
    label,
    multiline = false,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    ref,
    required = false,
    rows = 1,
    rowsMax,
    maxRows,
    minRows,
    select = false,
    SelectProps,
    type,
    value,
    variant = 'standard',
    ...other
  } = props;
  const {
    instruction = `Please input ${label}`,
    onEditorDialogReqested = () => void 0,
    onUpdated = () => void 0,
  } = props;
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
  const inputLabelId = label && id ? `${id}-label` : undefined;

  return (
    <FormControl
      className={className}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      hiddenLabel={hiddenLabel}
      ref={ref}
      required={required}
      color={color}
      variant={variant}
      {...other}
    >
      <InputLabel htmlFor={id} required={required} id={inputLabelId} {...InputLabelProps}>
        {label}
      </InputLabel>
      <Input
        aria-describedby={helperTextId}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        fullWidth={fullWidth}
        multiline={multiline}
        name={name}
        rows={rows}
        rowsMax={rowsMax}
        maxRows={maxRows}
        minRows={minRows}
        type={type}
        value={value}
        id={id}
        inputRef={inputRef}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        inputProps={inputProps}
        {...InputProps}
        inputComponent={VseHtmlInput as any}
      />
      {helperText && (
        <FormHelperText id={helperTextId} {...FormHelperTextProps}>
          {helperText}
        </FormHelperText>
      )}
      <IconButton
        color={iconColor}
        size={iconSize}
        className={classes.editButton}
        onClick={() => {
          onEditorDialogReqested({
            open: true,
            mode: props.mode,
            title: label,
            instruction: instruction,
            value: value as string,
            onUpdate: (content) => onUpdated(content),
          });
        }}><EditIcon /></IconButton>
    </FormControl>
  );
};
